import { gql } from "@apollo/client";
import {
  DataConnectionId,
  DataSourceFilter,
  OrgRole,
  convertDeprecatedDataSourceFilter,
  guardNever,
} from "@hex/common";
import { useMemo } from "react";

import { useCurrentUser } from "../../../../hooks/me/useCurrentUser";
import { ORG_ID } from "../../../../orgs";

import {
  useGetThreadsSettingsForEditorQuery,
  useGetThreadsSettingsForExplorerQuery,
} from "./useGetThreadsSettings.generated";

gql`
  query GetThreadsSettingsForExplorer($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        explorerDataSource
        explorerDefaultDataConnection {
          id
        }
      }
    }
  }

  query GetThreadsSettingsForEditor($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      defaultDataConnection {
        id
      }
      exploreAgentSettings {
        id
        editorEnabled
        editorDataSource
        editorDefaultDataConnection {
          id
        }
      }
    }
  }
`;

const ROLE_MAP: Record<OrgRole, "EDITOR" | "EXPLORER" | null> = {
  [OrgRole.ADMIN]: OrgRole.EDITOR,
  [OrgRole.MANAGER]: OrgRole.EDITOR,
  [OrgRole.EDITOR]: OrgRole.EDITOR,
  [OrgRole.EXPLORER]: OrgRole.EXPLORER,
  [OrgRole.MEMBER]: null,
  [OrgRole.GUEST]: null,
  [OrgRole.EMBEDDED_USER]: null,
  [OrgRole.ANONYMOUS]: null,
};

export interface ThreadsSettings {
  enabledForRole: boolean;
  dataSource: DataSourceFilter;
  defaultDataConnectionId: DataConnectionId | null;
  orgConfiguredDefaultDataConnectionId: DataConnectionId | null;
}

export const useGetThreadsSettings = (): {
  loading: boolean;
  settings: ThreadsSettings | null;
  minimumRoleForAccess: OrgRole | undefined;
} => {
  const user = useCurrentUser();
  const orgRole = user?.orgRole;

  const settingToLoad = orgRole ? ROLE_MAP[orgRole] : undefined;

  const { data: explorerSettingsData, loading: explorerSettingsLoading } =
    useGetThreadsSettingsForExplorerQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EXPLORER",
      // A permissions error can occur if the explorer doesn't have access to the set
      // default data connection. If that's the case, we want to still use the
      // rest of the data returned.
      errorPolicy: "all",
    });

  const { data: editorSettingsData, loading: editorSettingsLoading } =
    useGetThreadsSettingsForEditorQuery({
      variables: { orgId: ORG_ID },
      skip: settingToLoad !== "EDITOR",
      errorPolicy: "all",
    });

  const result = useMemo(() => {
    const minimumRoleForAccess = user?.org?.explorerEnabled
      ? OrgRole.EXPLORER
      : OrgRole.EDITOR;

    if (
      orgRole == null ||
      orgRole === OrgRole.EMBEDDED_USER ||
      orgRole === OrgRole.ANONYMOUS ||
      orgRole === OrgRole.MEMBER ||
      orgRole === OrgRole.GUEST
    ) {
      return { loading: false, settings: null, minimumRoleForAccess };
    }

    if (orgRole === OrgRole.ADMIN || orgRole === OrgRole.MANAGER) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return {
          loading: editorSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      const defaultDataConnectionId = org?.defaultDataConnection?.id ?? null;

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole: true,
          // Admins can always choose their data source but should default to the
          // editor configuration for consistency with the "restrict agent data
          // access" setting.
          dataSource: convertDeprecatedDataSourceFilter(
            settings.editorDataSource,
          ),
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EDITOR) {
      if (editorSettingsLoading || editorSettingsData == null) {
        return {
          loading: editorSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = editorSettingsData.orgById;
      const settings = org.exploreAgentSettings;
      const defaultDataConnectionId = org?.defaultDataConnection?.id ?? null;

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole: true,
          dataSource: convertDeprecatedDataSourceFilter(
            settings.editorDataSource,
          ),
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else if (orgRole === OrgRole.EXPLORER) {
      if (explorerSettingsLoading || explorerSettingsData == null) {
        return {
          loading: explorerSettingsLoading,
          settings: null,
          minimumRoleForAccess,
        };
      }

      const org = explorerSettingsData.orgById;
      const defaultDataConnectionId = org?.defaultDataConnection?.id ?? null;
      // Explorers always have access in endorsed mode (the only active flag variant).
      // The legacy explorerEnabled DB field is no longer consulted.
      const enabledForRole = true;
      // Explorers default to ENDORSED_DATA. The actual data source may change
      // based on org settings (enforceExplorerEndorsedMode) and user preferences
      // — see useThreadsDataSourceFilter for the full logic.
      const dataSource = DataSourceFilter.ENDORSED_DATA;

      return {
        loading: false,
        minimumRoleForAccess,
        settings: {
          enabledForRole,
          dataSource,
          defaultDataConnectionId,
          orgConfiguredDefaultDataConnectionId:
            org?.defaultDataConnection?.id ?? null,
        },
      };
    } else {
      guardNever(orgRole, orgRole);
      return { loading: false, settings: null, minimumRoleForAccess };
    }
  }, [
    explorerSettingsData,
    user?.org?.explorerEnabled,
    editorSettingsData,
    orgRole,
    editorSettingsLoading,
    explorerSettingsLoading,
  ]);

  return result;
};
